import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useGetDomainsWithLatestData } from "@/hooks/use-postmaster";
import { X } from "lucide-react";
import { useState } from "react";
import { SearchInput } from "../ui/search-input";

interface AddDomainDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (selectedDomains: string[]) => void;
}

export function AddDomainDialog({
  open,
  onOpenChange,
  onSubmit,
}: AddDomainDialogProps) {
  return (
    <Dialog open={open} onOpenChange={(open) => onOpenChange(open)}>
      <DialogContent className="max-w-2xl">
        <AddDomainDialogContent
          open={open}
          onOpenChange={onOpenChange}
          onSubmit={onSubmit}
        />
      </DialogContent>
    </Dialog>
  );
}

const AddDomainDialogContent = ({
  open,
  onOpenChange,
  onSubmit,
}: AddDomainDialogProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [page] = useState(1);
  const [limit] = useState(1000);
  const { data: domains, isLoading } = useGetDomainsWithLatestData({
    filters: {
      page,
      limit,
      search: searchTerm,
      isSynced: false,
    },
    enabled: open,
  });
  const [selectedDomains, setSelectedDomains] = useState<string[]>([]);

  const handleSelectAll = () => {
    if (selectedDomains.length === domains?.domains?.length) {
      setSelectedDomains([]);
    } else {
      setSelectedDomains(
        domains?.domains?.map((domain) => domain.domain) || []
      );
    }
  };

  const handleDomainSelect = (domainId: string) => {
    setSelectedDomains((prev) =>
      prev.includes(domainId)
        ? prev.filter((id) => id !== domainId)
        : [...prev, domainId]
    );
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>Add Domains</DialogTitle>
        {/* <div className="mt-4 space-y-3">
            <div className="flex items-center text-sm text-muted-foreground">
              <InfoIcon className="mr-2 h-4 w-4" />
              Each domain addition costs 5 credits
            </div>
            <div className="flex items-center justify-between rounded-md border bg-muted/50 p-3">
              <div className="text-sm">
                Available Credits: <span className="font-medium">100</span>
              </div>
              <div className="text-sm">
                Total Deduction:{" "}
                <span className="font-medium text-red-500">
                  {selectedDomains.length * 5} credits
                </span>
              </div>
            </div>
          </div> */}
      </DialogHeader>

      {selectedDomains.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">
              Selected: {selectedDomains.length}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedDomains([])}
              className="h-8 text-red-500 hover:text-red-600 hover:bg-red-50"
            >
              <X className="h-4 w-4 mr-1" />
              Clear All
            </Button>
          </div>
          <div className="flex flex-wrap gap-2 p-3 border rounded-md bg-muted/30 max-h-[80px] overflow-y-auto">
            {selectedDomains.map((domain) => (
              <Badge
                key={domain}
                variant="secondary"
                className="flex items-center gap-1.5 px-2 py-1"
              >
                <span className="text-xs">{domain}</span>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    handleDomainSelect(domain);
                  }}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* <div className="relative">
        <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search domains..."
          className="pl-8"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div> */}

      <SearchInput
        onSearch={(searchTerm) => setSearchTerm(searchTerm)}
        className="max-w-none sm:w-full"
        isLoading={isLoading}
      />
      <div className="border rounded-md max-h-[300px] overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-10">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900" />
          </div>
        ) : domains?.domains?.length && domains?.domains?.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[30px]">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300"
                    checked={
                      selectedDomains.length === domains?.domains?.length &&
                      domains?.domains?.length > 0
                    }
                    onChange={handleSelectAll}
                  />
                </TableHead>
                <TableHead className="whitespace-nowrap">Domain Name</TableHead>
                <TableHead className="whitespace-nowrap">
                  Google Postmaster
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {domains.domains.map((domain) => (
                <TableRow key={domain.domain}>
                  <TableCell>
                    <Input
                      type="checkbox"
                      className="rounded border-gray-300"
                      checked={selectedDomains.includes(domain.domain)}
                      onChange={() => handleDomainSelect(domain.domain)}
                    />
                  </TableCell>
                  <TableCell>{domain.domain}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2 h-full">
                      {domain.gpmStatus === "GPM Connected" ? (
                        <Badge
                          variant="secondary"
                          className="flex items-center gap-2 bg-green-500/10 text-green-500 hover:bg-green-500/10"
                        >
                          <span className="size-2 rounded-full bg-green-500" />
                          <span className="text-xs">GPM Connected</span>
                        </Badge>
                      ) : (
                        <Badge
                          variant="secondary"
                          className="flex items-center gap-2 bg-slate-500/10 text-slate-400 hover:bg-slate-500/10"
                        >
                          <span className="size-2 rounded-full bg-slate-400" />
                          <span className="text-xs">Not Connected</span>
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <div className="flex items-center justify-center py-10">
            <p className="text-sm text-muted-foreground">
              No Unsynced Domains Found
            </p>
          </div>
        )}
      </div>
      <DialogFooter>
        <Button
          variant="outline"
          onClick={() => {
            onOpenChange(false);
            setSelectedDomains([]);
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={() => {
            onSubmit(selectedDomains);
            setSelectedDomains([]);
          }}
          disabled={selectedDomains.length === 0}
        >
          Add Selected ({selectedDomains.length})
        </Button>
      </DialogFooter>
    </>
  );
};
